import { computed, effect, signal, type Signal } from '@preact/signals-react'
import type { GameCatalogDep, GameCatalogGame } from '../../catalog/gameCatalog/gameCatalog.types'
import type { GameCurationVM } from './gameCurationVM.types'

export type GameCurationVMContainer = {
  roomState: { curated: Signal<boolean>; curatedGameIds: Signal<string[]> }
  roomActions: {
    setCuratedMode(curated: boolean): Promise<void>
    setCuratedGames(gameIds: string[]): Promise<void>
  }
  gameCatalog: GameCatalogDep
  isOwner: Signal<boolean>
}

/**
 * Resolves a curated room's ordered selection into catalog games and edits it
 * for the owner. Curated games missing from this viewer's catalog (such as
 * the owner's preview-only games, for a participant) are looked up once.
 * The owner's edits show immediately, ahead of the room confirming them, so a
 * dragged game does not snap back while the change is saved.
 */
export function makeGameCurationVMDep(container: GameCurationVMContainer): GameCurationVM {
  const { roomState, roomActions, gameCatalog, isOwner } = container

  const isCurated = computed(() => roomState.curated.value)
  const canEdit = computed(() => isOwner.value)
  const showAll = signal(false)
  const lookedUp = signal<Map<string, GameCatalogGame>>(new Map())
  const requested = new Set<string>()
  const pendingIds = signal<string[] | null>(null)
  const curatedIds = computed(() => pendingIds.value ?? roomState.curatedGameIds.value)

  const catalogById = computed(() => new Map(gameCatalog.games.value.map((g) => [g.gameId, g])))

  const curatedGames = computed(() =>
    curatedIds.value.flatMap((gameId) => {
      const game = catalogById.value.get(gameId) ?? lookedUp.value.get(gameId)
      return game ? [game] : []
    }),
  )

  effect(() => {
    if (!isCurated.value) return
    const missing = roomState.curatedGameIds.value.filter((id) => !catalogById.value.has(id) && !requested.has(id))
    if (missing.length === 0) return
    for (const id of missing) requested.add(id)
    void gameCatalog.lookupGames(missing).then((games) => {
      const next = new Map(lookedUp.value)
      for (const game of games) next.set(game.gameId, game)
      lookedUp.value = next
    })
  })

  const save = async (gameIds: string[]) => {
    pendingIds.value = gameIds
    try {
      await roomActions.setCuratedGames(gameIds)
    } finally {
      // Fall back to the room's selection, which reverts a refused edit.
      if (pendingIds.value === gameIds) pendingIds.value = null
    }
  }
  const ids = () => curatedIds.value

  return {
    isCurated,
    canEdit,
    curatedGames,
    showAll,
    actions: {
      toggleShowAll: () => {
        showAll.value = !showAll.value
      },
      setCurated: (curated) => roomActions.setCuratedMode(curated),
      add: async (gameId) => {
        if (!ids().includes(gameId)) await save([...ids(), gameId])
      },
      remove: (gameId) => save(ids().filter((id) => id !== gameId)),
      move: async (gameId, toIndex) => {
        const current = ids()
        const from = current.indexOf(gameId)
        const to = Math.min(Math.max(toIndex, 0), current.length - 1)
        if (from < 0 || from === to) return
        const next = [...current]
        next.splice(from, 1)
        next.splice(to, 0, gameId)
        await save(next)
      },
    },
  }
}
